// Notifications: the event catalogue, the default emails and how they're filled. The SINGLE source of
// truth for the worker (who gets what, and the emails it sends) and the web (the admin Notifications page
// and its preview, and where a bell notification takes you). Add an event here and both sides get it:
// the key list drives the config and the admin page, NOTIFY_EVENT_META forces a label, description,
// fields and link for it, and NOTIFY_DEFAULT_TEMPLATES a default email.
//
// Every email goes out as plain text with {placeholder} tokens filled from the event's variables
// (renderTemplate), then wrapped in the shared layout (emailLayout.ts). An admin can replace an event's
// subject and/or body in admin → Notifications; a blank one sends the default below.
import { serviceVesselLink } from './domain/serviceNewVessel.js';
export const NOTIFY_EVENT_KEYS = [
    'order_confirmed',
    'order_changed',
    'order_accepted',
    'order_locked',
    'order_unlocked',
    'order_identity_edit',
    'new_user',
    'grp_new_vessel',
    'grp_status_green',
    'assembly_deck_cap',
    'hin_edited',
    'assembly_coord_daily',
    'service_request_new',
    'service_new_vessel',
    'ticket_new',
    'ticket_decided',
];
/** where someone sees their own tickets: the Tickets card in Settings */
export const TICKETS_LINK = '/settings#tickets';
const orderLink = (v) => (v.orderNumber ? `/sales/orders?q=${encodeURIComponent(v.orderNumber)}` : '/sales/orders');
// an order Assembly has to review: Assembly Orders, opened on it
const assemblyLink = (v) => (v.orderNumber ? `/assembly-orders?q=${encodeURIComponent(v.orderNumber)}` : '/assembly-orders');
const VESSEL_FIELDS = ['vessel', 'sap', 'name', 'model', 'layout', 'headline'];
// what an order email can say about the order (worker orders/service.ts orderVars)
const ORDER_FIELDS = ['orderNumber', 'actor', 'model', 'customer', 'vessel', 'sap', 'dealer', 'delivery', 'total', 'revision', 'optionCount', 'options', 'removedCount', 'removed', 'requestCount', 'requests'];
const CREATOR = 'The person who created the order';
export const NOTIFY_EVENT_META = {
    order_confirmed: {
        label: 'Order confirmed: to accept',
        desc: 'Sent to Assembly when Sales confirms an order in the configurator. It waits in Assembly Orders until Assembly reviews and accepts it.',
        fields: ORDER_FIELDS,
        kicker: 'Order to accept',
        link: assemblyLink,
        action: 'Review and accept',
    },
    order_changed: {
        label: 'Order changed: new revision to accept',
        desc: 'Sent to Assembly when a confirmed order is changed and saved as a new revision. The new revision waits in Assembly Orders until Assembly accepts it.',
        fields: [...ORDER_FIELDS, 'changeCount', 'changes', 'production'],
        kicker: 'Revision to accept',
        link: assemblyLink,
        action: 'Review the changes',
    },
    order_accepted: {
        label: 'Order accepted by Assembly',
        desc: 'Sent to the person who created an order when Assembly accepts it (or a new revision of it).',
        fields: ORDER_FIELDS,
        kicker: 'Your order',
        audience: CREATOR,
        link: orderLink,
        action: 'Open the order',
    },
    order_locked: {
        label: 'Order in production',
        desc: 'Sent to the person who created an order when Assembly puts it in production (it is then locked), or takes it out.',
        fields: [...ORDER_FIELDS, 'state', 'what'],
        kicker: 'Your order',
        audience: CREATOR,
        link: orderLink,
        action: 'Open the order',
    },
    order_unlocked: {
        label: 'Order opened for Sales',
        desc: 'Sent to the person who created an order when Assembly opens it for them to change, with the reason and until when.',
        fields: ['orderNumber', 'actor', 'model', 'customer', 'vessel', 'sap', 'revision', 'reason', 'instructions', 'until'],
        kicker: 'Your order',
        audience: CREATOR,
        link: orderLink,
        action: 'Change the order',
    },
    order_identity_edit: {
        label: 'Customer / Vessel / SAP edited',
        desc: "Sent to Assembly when an order's customer, vessel name or SAP number is changed (the order file follows).",
        fields: ['orderNumber', 'actor', 'model', 'changes'],
        kicker: 'Order details',
        link: assemblyLink,
        action: 'Open the order',
    },
    new_user: {
        label: 'New user joins (first sign-in)',
        desc: 'Sent when a new person signs in for the first time.',
        fields: ['email'],
        kicker: 'Users',
        link: () => '/admin/users',
        action: 'Set them up',
    },
    grp_new_vessel: {
        label: 'GRP - new vessel added',
        desc: 'Sent when a new vessel is added in GRP (its layout is set) - after a 10-minute delay.',
        fields: VESSEL_FIELDS,
        kicker: 'GRP inspection',
        link: () => '/grp',
        action: 'Open GRP Inspection',
    },
    grp_status_green: {
        label: 'GRP - status turned green',
        desc: "Sent when a vessel's GRP status turns green.",
        fields: VESSEL_FIELDS,
        kicker: 'GRP inspection',
        link: () => '/grp',
        action: 'Open GRP Inspection',
    },
    assembly_deck_cap: {
        label: 'Assembly - “deck cap fitted” task checked',
        desc: 'Sent when the “deck cap fitted” task is checked on the assembly sheet.',
        fields: VESSEL_FIELDS,
        kicker: 'Assembly',
        link: () => '/deck',
        action: 'Open Deck',
    },
    hin_edited: {
        label: 'Vessel ID - HIN changed',
        desc: "Sent when a vessel's HIN is changed on the Vessel Identification page.",
        fields: ['actor', 'sap', 'name', 'oldHin', 'newHin', 'reason'],
        kicker: 'Vessel identification',
        link: () => '/vessel-identification',
        action: 'Open Vessel Identification',
    },
    assembly_coord_daily: {
        label: 'Assembly coordinators - daily check-in',
        desc: 'A summary of which assembly coordinators checked in and how many assembly tasks each ticked or unticked in Assembly Progress, sent each day after 23:30 (Athens time). Defaults to administrators + moderators.',
        fields: ['date', 'activeCount', 'count', 'summary'],
        kicker: 'Daily summary',
        link: () => null,
    },
    service_request_new: {
        label: 'Client service request received',
        desc: 'Sent when a client books a service through the portal. Defaults to the Service role plus administrators and moderators.',
        fields: ['ref', 'vessel', 'client', 'email', 'phone', 'hin', 'preferredDate', 'details'],
        kicker: 'Service request',
        link: () => '/service-requests',
        action: 'Approve or decline',
    },
    service_new_vessel: {
        label: 'New vessel for service',
        desc: 'Sent when someone outside the Service team (Sales, for example) adds a vessel to the Service sheet, in Rolodex or straight in Smartsheet. Every new vessel is flagged, whoever adds it. Defaults to the Service role.',
        fields: ['vessel', 'model', 'sap', 'group', 'addedBy'],
        kicker: 'Service',
        // {rowId} isn't offered as a placeholder: it only points the button and the bell at the vessel
        link: (v) => (v.rowId ? serviceVesselLink(v.rowId) : '/service'),
        action: 'Open the vessel',
    },
    ticket_new: {
        label: 'Ticket sent',
        desc: 'Sent when someone sends a ticket from Settings: a problem, a request for a feature or access, or a correction to data. Defaults to administrators.',
        fields: ['ref', 'actor', 'category', 'priority', 'subject', 'page', 'details'],
        kicker: 'Ticket',
        link: () => '/admin/tickets',
        action: 'Accept or deny',
    },
    ticket_decided: {
        label: 'Ticket accepted or denied',
        desc: 'Sent to the person who sent a ticket when an administrator accepts or denies it, with their reply.',
        fields: ['ref', 'subject', 'decision', 'actor', 'reply'],
        kicker: 'Your ticket',
        audience: 'The person who sent the ticket',
        link: () => TICKETS_LINK,
        action: 'See your tickets',
    },
};
// The default email for each event. Detail lines ("SAP: {sap}") whose value is empty are left out of
// the email, so one template reads right however much is known (see renderTemplate).
export const NOTIFY_DEFAULT_TEMPLATES = {
    order_confirmed: {
        subject: 'New order {orderNumber} to accept · {model} · {customer}',
        body: '{actor} confirmed order {orderNumber} in the configurator. It is waiting in Assembly Orders for you to review and accept it.\n\nModel: {model}\nCustomer: {customer}\nVessel: {vessel}\nSAP: {sap}\nDealer: {dealer}\nDelivery: {delivery}\nTotal: {total}\n\nOptions ({optionCount}):\n{options}\n\nRemoved from standard ({removedCount}):\n{removed}\n\nSpecial requests ({requestCount}):\n{requests}',
    },
    order_changed: {
        subject: 'Order {orderNumber} changed: revision {revision} to accept',
        body: '{actor} changed order {orderNumber} and saved it as revision {revision}. Review the changes in Assembly Orders and accept them.\n{production}\n\nModel: {model}\nCustomer: {customer}\nVessel: {vessel}\nSAP: {sap}\nTotal: {total}\n\nWhat changed ({changeCount}):\n{changes}',
    },
    order_accepted: {
        subject: 'Order {orderNumber} accepted by Assembly',
        body: '{actor} accepted revision {revision} of order {orderNumber}: Assembly will build from it.\n\nModel: {model}\nCustomer: {customer}\nVessel: {vessel}\nSAP: {sap}\n\nIt stays locked: to change it, ask Assembly to open it for you.',
    },
    order_locked: {
        subject: 'Order {orderNumber} {state}',
        body: 'Order {orderNumber} {state}: {actor} changed it in Assembly Orders.\n\nModel: {model}\nCustomer: {customer}\nVessel: {vessel}\nSAP: {sap}\nRevision: {revision}\n\n{what}',
    },
    order_unlocked: {
        subject: 'Order {orderNumber} is open for you to change',
        body: '{actor} opened order {orderNumber} so you can change it. Save your changes once, as a new revision: it then locks again and goes back to Assembly to accept.\n\nModel: {model}\nCustomer: {customer}\nVessel: {vessel}\nReason: {reason}\nInstructions: {instructions}\nOpen until: {until}',
    },
    order_identity_edit: {
        subject: 'Order {orderNumber}: customer, vessel or SAP changed',
        body: '{actor} changed the details of order {orderNumber}. Its Order Sheet row and order file follow.\n\nModel: {model}\n\nWhat changed:\n{changes}',
    },
    new_user: {
        subject: 'New user waiting for access: {email}',
        body: '{email} signed in to Rolodex for the first time and is waiting for access. Give them a role and a department in Administration, Users.',
    },
    grp_new_vessel: {
        subject: 'New vessel in GRP: {vessel}',
        body: '{vessel} has been added to the GRP inspection sheet.\n\nSAP: {sap}\nName: {name}\nModel: {model}\nLayout: {layout}',
    },
    grp_status_green: {
        subject: 'GRP status green: {vessel}',
        body: 'The GRP status of {vessel} has turned green.\n\nSAP: {sap}\nName: {name}\nModel: {model}\nLayout: {layout}',
    },
    assembly_deck_cap: {
        subject: 'Deck cap fitted: {vessel}',
        body: 'The deck cap has been fitted on {vessel}: the task was ticked in Assembly Progress.\n\nSAP: {sap}\nName: {name}\nModel: {model}\nLayout: {layout}',
    },
    hin_edited: {
        subject: 'HIN changed for SAP {sap} · {name}',
        body: '{actor} changed the HIN of SAP {sap}.\n\nVessel: {name}\nSAP: {sap}\nOld HIN: {oldHin}\nNew HIN: {newHin}\nReason: {reason}',
    },
    assembly_coord_daily: {
        subject: 'Assembly coordinators on {date}: {activeCount} of {count} checked in',
        body: '{activeCount} of {count} assembly coordinators checked in on {date}.\n\nWho checked in:\n{summary}',
    },
    service_request_new: {
        subject: 'New service request {ref}: {vessel}',
        body: 'A client booked a service through the client portal.\n\nRef: {ref}\nVessel: {vessel}\nClient: {client}\nEmail: {email}\nPhone: {phone}\nHIN: {hin}\nPreferred date: {preferredDate}\n\nWhat they need:\n{details}',
    },
    service_new_vessel: {
        subject: 'New vessel for service: {vessel}',
        body: '{vessel} was added to the Service sheet and has been flagged for the Service team.\n\nVessel: {vessel}\nModel: {model}\nSAP: {sap}\nGroup: {group}\nAdded by: {addedBy}',
    },
    ticket_new: {
        subject: 'Ticket {ref} from {actor}: {subject}',
        body: '{actor} sent ticket {ref}: {subject}\n\nWhat: {category}\nHow urgent: {priority}\nPage: {page}\n\n{details}',
    },
    ticket_decided: {
        subject: 'Your ticket {ref} was {decision}: {subject}',
        body: '{actor} {decision} your ticket {ref}: {subject}\n\n{reply}',
    },
};
/** Representative values for every placeholder, for test emails and the admin page's preview. */
export const NOTIFY_SAMPLE_VARS = {
    orderNumber: '12345',
    actor: 'Maria Papadopoulou',
    model: 'TG-38',
    customer: 'Sample Customer',
    vessel: 'Hull 42',
    sap: '9100334',
    revision: '2',
    changeCount: '3',
    changes: '• Engine: 2x300 → 2x350\n• Hull colour: White → Grey\n• Added: Teak cockpit floor',
    dealer: 'Sample Marine',
    delivery: 'May 2027',
    total: '€248,000.00',
    optionCount: '3',
    options: '• Teak cockpit floor\n• Garmin 16” screen × 2\n• Bow thruster',
    removedCount: '1',
    removed: '• Standard cockpit cushions',
    requestCount: '1',
    requests: '• Logo embroidered on the cushions',
    production: 'It is in production: the floor keeps working from the accepted revision until you accept this one.',
    what: 'The boat is being built from it, so it is locked: ask Assembly if anything needs to change.',
    state: 'is now in production',
    reason: 'Customer asked for a different engine',
    instructions: 'Change the engine only',
    until: '08/10/2026 18:00',
    email: 'new.user@technohull.com',
    headline: 'New vessel added to GRP',
    name: 'Hull 42',
    layout: 'Open',
    oldHin: 'GR-ABC12345A123',
    newHin: 'GR-XYZ98765B321',
    date: '30/09/2026',
    activeCount: '4',
    count: '6',
    summary: 'Nikos Papadakis: checked in at 08:15, 12 assembly tasks updated\nMaria Georgiou: did not check in',
    ref: 'SR-1001',
    client: 'Sample Client',
    phone: '+30 210 0000000',
    hin: 'GR-ABC12345A123',
    preferredDate: '15/10/2026',
    details: 'Engine service and full hull inspection before the summer season.',
    group: 'Warranty 2026',
    addedBy: 'Maria Papadopoulou',
    category: 'Something isn’t working',
    priority: 'Normal',
    subject: 'The Washing Plan printout cuts off the last day',
    page: 'Washing Plan',
    decision: 'accepted',
    reply: 'Thanks, we will fix it in the next version.',
};
const TOKEN = /\{(\w+)\}/g;
// what's left of a line once its (empty) tokens are taken out, when it was only a label or separators
const BARE_LINE = /^\s*(?:[^:\n]{1,40}:)?[\s·•,;:|/()\-–—]*$/;
/** tidy a line whose tokens came out empty: "TG-38 ·  · Hull" → "TG-38 · Hull", no dangling "·". */
const tidy = (line) => line.replace(/(?:\s*·\s*){2,}/g, ' · ').replace(/^\s*·\s*/, '').replace(/\s*·\s*$/, '').replace(/\s+$/, '');
/** Fill {token}s from `vars` (unknown tokens become empty). A line with tokens that all came out empty
 *  is left out when nothing but a label or separators would remain ("SAP: {sap}" with no SAP), and
 *  empty separators are tidied away, so one template reads right however much is known. */
export function renderTemplate(tpl, vars) {
    const out = [];
    for (const line of (tpl || '').replace(/\r\n/g, '\n').split('\n')) {
        let tokens = 0;
        let filled = 0;
        const rendered = line.replace(TOKEN, (_m, k) => {
            tokens++;
            const v = vars[k] ?? '';
            if (v.trim())
                filled++;
            return v;
        });
        if (tokens && !filled && BARE_LINE.test(line.replace(TOKEN, '')))
            continue;
        out.push(tokens ? tidy(rendered) : rendered);
    }
    return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
/** the first paragraph of a rendered body (the bell's message by default). */
export const firstParagraph = (text) => (text || '').trim().split(/\n\s*\n/)[0] ?? '';
/** where a bell notification opens: its own link, else its event's page, else (an old order row) Sales. */
export function notificationLink(n) {
    if (n.link)
        return n.link;
    if (n.notification_type === 'agenda_mention')
        return '/agenda';
    const meta = NOTIFY_EVENT_META[n.notification_type];
    const link = meta?.link({}) ?? null;
    return link ?? (n.order_id ? '/sales/orders' : null);
}
/** Build an event's email: the admin's custom subject/body where set (each on its own), else the
 *  default, filled from `vars`, with the event's kicker, a button to its page and the footer's reason. */
export function buildNotifyEmail(event, vars, opts = {}) {
    const meta = NOTIFY_EVENT_META[event];
    const def = NOTIFY_DEFAULT_TEMPLATES[event];
    const subjectTpl = opts.custom?.subject?.trim() ? opts.custom.subject : def.subject;
    const bodyTpl = opts.custom?.body?.trim() ? opts.custom.body : def.body;
    const link = opts.link !== undefined ? opts.link : meta.link(Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, v ?? ''])));
    const base = (opts.appUrl ?? '').replace(/\/+$/, '');
    return {
        subject: renderTemplate(subjectTpl, vars).replace(/\s*\n\s*/g, ' '),
        body: renderTemplate(bodyTpl, vars),
        kicker: meta.kicker,
        action: link && base ? { label: meta.action ?? 'Open in Rolodex', url: base + link } : undefined,
        audience: 'staff',
        reason: `You get this because you're on the list for “${meta.label}” in Rolodex. An administrator can change that in Administration, Notifications.`,
    };
}
//# sourceMappingURL=notify.js.map